import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, map, merge, of, startWith, switchMap } from "rxjs";
import { CoreMediaContentRendererComponent } from "./coremedia-content-renderer";
import { CoreMediaBreadcrumbItem } from "../../../navigation/data/coremedia-navigation.model";
import { navigationStore } from "../../../navigation/data/navigation.store";
import { CoreMediaBreadcrumbComponent } from "../../../navigation/ui/coremedia-breadcrumb/coremedia-breadcrumb";
import { NotFoundPageComponent } from "../not-found/not-found-page";

/** Discriminated state for the async path -> content-ID resolution. */
type ContentIdState =
  | { status: "loading" }
  | {
      status: "resolved";
      id: string | null;
      siteName?: string;
      breadcrumbs?: readonly CoreMediaBreadcrumbItem[] | null;
    };

@Component({
  selector: "claas-content-page",
  template: `
    @if (showNotFound()) {
      <claas-not-found-page />
    } @else if (contentId(); as id) {
      @if (breadcrumbItems(); as items) {
        <claas-coremedia-breadcrumb [items]="items" />
      }
      <claas-coremedia-content-renderer
        [contentId]="id"
        [contentSiteName]="contentSiteName()"
        (notFound)="onRendererNotFound()"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CoreMediaBreadcrumbComponent,
    CoreMediaContentRendererComponent,
    NotFoundPageComponent,
  ],
})
export class ContentPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly navigation = inject(navigationStore);

  /** Set when the renderer itself reports a failed/empty channel (incl. any backend error). */
  private readonly rendererNotFound = signal(false);

  private readonly navigationStatus$ = toObservable(this.navigation.status);

  private readonly contentIdState = toSignal(
    merge(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
      this.navigationStatus$,
    ).pipe(
      startWith(null),
      switchMap(() => {
        const idFromCmsRoute = this.route.snapshot.paramMap.get("id");
        if (idFromCmsRoute) {
          return of<ContentIdState>({ status: "resolved", id: idFromCmsRoute });
        }
        if (
          this.navigation.status() === "idle" ||
          this.navigation.status() === "loading"
        ) {
          return of<ContentIdState>({ status: "loading" });
        }
        if (this.navigation.status() === "error") {
          return of<ContentIdState>({ status: "resolved", id: null });
        }
        const fullPath = (this.router.url || "/").split(/[?#]/)[0] || "/";
        const prefix = this.route.snapshot.data["routePrefix"] ?? "cms";
        const contentPath =
          fullPath.replace(new RegExp(`^\\/${prefix}(?=\\/|$)`), "") || "/";
        return this.navigation
          .resolvePageByPath(contentPath, `/${prefix}`)
          .pipe(
            map(
              (result): ContentIdState => ({
                status: "resolved",
                id: result?.id ?? null,
                siteName: result?.siteName,
                breadcrumbs: prefix === "cms" ? result?.breadcrumbs : null,
              }),
            ),
            startWith<ContentIdState>({ status: "loading" }),
          );
      }),
    ),
    { initialValue: { status: "loading" } as ContentIdState },
  );

  readonly contentId = computed(() => {
    const state = this.contentIdState();
    return state.status === "resolved" ? state.id : null;
  });

  readonly contentSiteName = computed(() => {
    const state = this.contentIdState();
    return state.status === "resolved" ? state.siteName : undefined;
  });

  readonly breadcrumbItems = computed(() => {
    const state = this.contentIdState();
    return state.status === "resolved" && state.breadcrumbs?.length
      ? state.breadcrumbs
      : null;
  });

  /** Unknown paths (path resolution -> null) OR a failed/empty channel both show the 404 page. */
  readonly showNotFound = computed(() => {
    const state = this.contentIdState();
    return (
      this.rendererNotFound() ||
      (state.status === "resolved" && state.id === null)
    );
  });

  constructor() {
    // Reset the renderer-reported not-found flag on every new navigation attempt.
    effect(() => {
      this.contentIdState();
      this.rendererNotFound.set(false);
    });
  }

  onRendererNotFound(): void {
    this.rendererNotFound.set(true);
  }
}
